/**
 * French catalog — the SOURCE of truth for the i18n foundation.
 *
 * Because the app is authored in French, this file holds the real product copy.
 * Its inferred type becomes `TranslationKeys` (see the bottom of the file), and
 * every other locale (`en`, `ar`) is declared `: TranslationKeys`, so TypeScript
 * fails the build if a translated catalog is missing a key or adds an unknown
 * one — that is the primary, compile-time structural-parity guard.
 *
 * Nested by namespace: `common` (app-wide buttons/labels), `nav` (the shell —
 * sidebar, header, footer), `settings` (the Settings tab bar + header) and
 * `profile` (the Profile page). Keep leaf values plain strings; use `{{var}}`
 * for interpolation placeholders (see useT).
 */

// NOTE: no `as const` — object-literal property values widen to `string`, which
// is exactly what we want: `TranslationKeys` (typeof this object) then describes
// the *shape* with string leaves, so `en`/`ar` need matching structure, not the
// identical French literals.
const fr = {
  /** App-wide buttons, verbs and status words reused across many surfaces. */
  common: {
    save: 'Enregistrer',
    cancel: 'Annuler',
    delete: 'Supprimer',
    create: 'Créer',
    edit: 'Modifier',
    search: 'Rechercher',
    loading: 'Chargement…',
    confirm: 'Confirmer',
    close: 'Fermer',
    back: 'Retour',
    next: 'Suivant',
    previous: 'Précédent',
    add: 'Ajouter',
    remove: 'Retirer',
    apply: 'Appliquer',
    reset: 'Réinitialiser',
    refresh: 'Actualiser',
    retry: 'Réessayer',
    duplicate: 'Dupliquer',
    export: 'Exporter',
    import: 'Importer',
    copy: 'Copier',
    download: 'Télécharger',
    upload: 'Téléverser',
    filter: 'Filtrer',
    all: 'Tous',
    none: 'Aucun',
    yes: 'Oui',
    no: 'Non',
    ok: 'OK',
    actions: 'Actions',
    more: 'Plus',
    settings: 'Paramètres',
    processing: 'Traitement…',
    readOnly: 'Lecture seule',
    empty: 'Aucun élément',
  },

  /** The application shell: sidebar navigation, header controls and footer. */
  nav: {
    brand: 'PubSocialNet',
    // Sidebar group headers.
    groups: {
      general: 'Général',
      creation: 'Création',
      prospection: 'Prospection',
      ai: 'IA',
      system: 'Système',
    },
    // Sidebar item labels (one per route).
    items: {
      dashboard: 'Tableau de bord',
      content: 'Contenu',
      calendar: 'Calendrier',
      publishing: 'Publication',
      contentQueue: 'File de contenu',
      bulkSchedule: 'Planification en masse',
      platforms: 'Plateformes',
      ads: 'Publicités',
      mediaLibrary: 'Médiathèque',
      analytics: 'Analytics',
      design: 'Design',
      workflows: 'Workflows',
      crm: 'CRM & Leads',
      discovery: 'Découverte',
      prospectionRecommend: 'Recommandations',
      prospectionCalendar: 'Calendrier prospection',
      accountIntelligence: 'Intelligence de compte',
      carouselEditor: 'Éditeur de carrousel',
      richContentStudio: 'Studio de contenu riche',
      prospect: 'Base B2B',
      marketplace: 'Marketplace',
      social: 'Leads sociaux',
      outreach: 'Email Outreach',
      inbox: 'Inbox sociale',
      community: 'Community',
      listening: 'Écoute sociale',
      bio: 'Link-in-bio',
      projects: 'Projets',
      aiTeam: 'Équipe IA',
      aiOffice: 'Bureau IA',
      aiTeams: 'Équipes IA',
      aiDeliverables: 'Livrables IA',
      aiSchedules: 'Planifications IA',
      aiControl: 'Contrôle IA',
      aiAuditTrail: 'Journal d\'audit IA',
      staffQuestions: 'Questions des agents',
      grants: 'Demandes de droits',
      superadminFleet: 'Flotte plateforme',
      orchestration: 'Orchestration IA',
      aiAssistant: 'Assistant IA',
      contentStudio: 'Studio de contenu',
      ghostwriter: 'Ghostwriter IA',
      repurpose: 'Repurposing',
      hookLibrary: 'Bibliothèque de hooks',
      brandVoice: 'Voix de marque',
      abCopy: 'Tests A/B copy',
      suggestions: 'Suggestions IA',
      personalization: 'Personnalisation',
      communityAi: 'Community IA',
      ragDocuments: 'Documents RAG',
      memory: 'Mémoire IA',
      approvals: 'Approbations',
      aiActivity: 'Activité IA',
      settings: 'Paramètres',
    },
    // Header controls.
    header: {
      openMenu: 'Ouvrir le menu',
      closeMenu: 'Fermer le menu',
      toggleSidebar: 'Réduire ou déplier la barre latérale',
      breadcrumbRoot: 'PubSocialNet',
      breadcrumbAria: 'Fil d’Ariane',
      searchPlaceholder: 'Rechercher…',
      openCommandPalette: 'Ouvrir la palette de commandes',
      quickActions: 'Actions rapides',
      create: 'Créer',
      createItem: 'Créer un élément',
      shortcuts: 'Raccourcis clavier',
      showShortcuts: 'Afficher les raccourcis clavier',
    },
    // "＋ Créer" quick-create menu.
    quickCreate: {
      newContent: 'Nouveau contenu',
      newContact: 'Nouveau contact',
      newCompany: 'Nouvelle entreprise',
      newCampaign: 'Nouvelle campagne',
      newProject: 'Nouveau projet',
      newDesign: 'Nouveau design',
    },
    // Quick-access toolbar.
    quickAccess: {
      search: 'Rechercher (⌘K)',
      newContent: 'Nouveau contenu',
      newContact: 'Nouveau contact',
      newDeal: 'Nouveau deal',
      inbox: 'Inbox sociale',
      assistant: 'Assistant IA',
    },
    // User dropdown.
    user: {
      fallbackName: 'Utilisateur',
      connectedAccount: 'Compte connecté',
      settings: 'Paramètres',
      signout: 'Déconnexion',
    },
    // Footer.
    footer: {
      environment: 'Environnement d’exécution',
      whatsNew: 'Nouveautés',
      shortcuts: 'Raccourcis',
      privacy: 'Confidentialité',
      links: 'Liens du pied de page',
      statusTitle: 'État du service (sondage /health toutes les 30 s)',
      operational: 'Opérationnel',
      unavailable: 'Service indisponible',
      checking: 'Vérification…',
    },
    // Brand-navigation aria label (mobile drawer).
    primaryNav: 'Navigation principale',
  },

  /** Settings shell — the tab bar and page header. */
  settings: {
    title: 'Paramètres',
    subtitle:
      'Crédits IA & facturation, registre des agents et modèles, équipe, profil et connexions sociales.',
    navAria: 'Navigation Paramètres',
    tabs: {
      credits: 'Crédits & Facturation',
      subscription: 'Abonnement',
      referral: 'Parrainage',
      aiRegistry: 'Registre IA',
      governance: 'Gouvernance IA',
      connectors: 'Connecteurs',
      developers: 'API développeur',
      integrations: 'Intégrations',
      connections: 'Comptes connectés',
      team: 'Équipe',
      profile: 'Profil',
    },
  },

  /** The Profile page (/settings/profile). */
  profile: {
    tabsAria: 'Sections du profil',
    tabProfile: 'Profil',
    tabConnections: 'Connexions',
    emailVerified: 'E-mail vérifié',
    emailUnverified: 'E-mail non vérifié',
    noRole: 'aucun rôle',
    stats: {
      roles: 'Rôles',
      session: 'Session',
      expiresAt: 'expire à',
    },
    account: {
      title: 'Compte',
      name: 'Nom',
      email: 'E-mail',
      username: 'Identifiant',
      tenant: 'Locataire (tenant)',
      userId: 'ID utilisateur',
      roles: 'Rôles',
    },
    prefs: {
      title: 'Préférences',
      language: 'Langue',
      languageHint: 'Préférence mémorisée sur cet appareil.',
      theme: 'Thème',
      themeHint: '« Système » suit le réglage clair/sombre du navigateur.',
      themeSystem: 'Système',
      themeLight: 'Clair',
      themeDark: 'Sombre',
      appearance: 'Apparence',
      appearanceHint:
        'Bascule claire/sombre et couleur d’accent — également accessibles depuis l’en-tête.',
    },
  },

  /**
   * Platforms/Connections surfaces (/platforms and /settings/profile →
   * Connexions). The rest of these two pages is not yet wired into this
   * catalog (hardcoded French, matching the app's current authoring
   * language) — only the connection-health messaging below is i18n'd.
   */
  platforms: {
    connections: {
      needsReconnectBadge: 'Reconnexion requise',
      needsReconnectHint:
        'Le jeton stocké est illisible côté serveur — cette connexion ne peut plus publier. Reconnectez le compte pour la réparer.',
      reconnectAction: 'Reconnecter',
    },
  },
};

export default fr;

/**
 * The structural contract every locale must satisfy. Because `fr` has no
 * `as const`, its leaves are `string`, so this type describes the catalog SHAPE
 * (namespaces + keys, string leaves). `en`/`ar` declared `: TranslationKeys`
 * are then checked for exact structural parity at compile time.
 */
export type TranslationKeys = typeof fr;
